import { ModerationScanCard, type ModerationScan } from '../sessions/moderation';
import React, { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Lock, ChevronDown, FileLock2, Wrench, MessagesSquare, MessageCircle, Shield, Sparkles, ListChecks } from 'lucide-react';
import useApi from '../api';
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'react-toastify';
import { useUser } from '../user/UserContext';
import type { LinkedQuestion } from '../types';

interface ContextTool {
  name?: string;
  description?: string;
  visibility?: string;
}

type ContentBlock =
  | { type: 'text'; markdown?: string }
  | { type: 'redacted'; reason?: string };

interface ConversationEntry {
  role?: string;
  kind?: 'text' | 'tool_call' | 'tool_output';
  content?: string;
  tool_name?: string;
  args?: any;
  redacted?: boolean;
}

interface ContextInputMessage {
  id?: string | null;
  role?: string;
  type?: string;
  content?: string | null;
  synthetic?: boolean;
  missing?: boolean;
}

interface ContextInput {
  messages?: ContextInputMessage[];
  pruned?: { call_id?: string; query?: string; reason?: string; type?: string }[];
  previous_response_id?: string | null;
}

interface ContextSnapshot {
  id: string;
  message_id?: string;
  // customer view
  query?: string | null;
  previous_messages?: ConversationEntry[];
  intermediate_steps?: ConversationEntry[];
  content?: ContentBlock[];
  // admin view
  system_segments?: { text?: string }[];
  input?: ContextInput | null;
  model?: string;
  provider?: string;
  tools?: ContextTool[];
  created_at?: string;
  // Session moderation scan of the exchange this generation closed.
  moderation_scan?: ModerationScan | null;
}

interface ContextDrawerProps {
  messageId: string;
  open: boolean;
  onClose: () => void;
  /** The AI reply this snapshot produced (already rendered in the transcript). */
  responseContent?: string;
  /** For tool-call turns: the decision the AI made (instead of a text reply). */
  toolCall?: { name?: string; args?: any };
  /** Admin view shows raw internal segments + the conversation replay. */
  isAdmin?: boolean;
  /** False when the message predates context capture: skip the fetch and only
   *  offer the correction panel. Defaults to true. */
  hasSnapshot?: boolean;
  /** The knowledge-base question linked to this reply, if the extraction run
   *  flagged it or a reviewer already corrected it. */
  question?: LinkedQuestion | null;
  /** Reflect a saved correction back into the transcript. */
  onQuestionChange?: (question: LinkedQuestion) => void;
  /** The visitor message this reply answered; prefills a fresh correction when
   *  the snapshot has no query of its own. */
  visitorQuestion?: string | null;
}

const SOURCE_URL_RE = /^URL:\s*(\S+)/gm;

/** Pages the AI searched for this reply (from its search_knowledge outputs),
 *  shown so the reviewer can fix the website instead of a shadow answer. */
const extractSourceUrls = (steps: ConversationEntry[]): string[] => {
  const urls: string[] = [];
  for (const step of steps) {
    if (step.kind !== 'tool_output' || step.tool_name !== 'search_knowledge' || !step.content) continue;
    for (const match of step.content.matchAll(SOURCE_URL_RE)) {
      if (match[1] && !urls.includes(match[1])) urls.push(match[1]);
    }
  }
  return urls.slice(0, 5);
};

const reviewerName = (q: LinkedQuestion | null | undefined): string | null => {
  const by = q?.answered_by;
  if (!by) return null;
  return [by.first_name, by.last_name].filter(Boolean).join(' ') || by.email || 'a teammate';
};

/**
 * Correct the AI's answer in place. When the extraction run linked a question
 * to this reply, it is prefilled; otherwise the reviewer writes both halves and
 * the new pair is linked to this message. Saved pairs are org-wide: the agent
 * prefers them over anything it would synthesize from pages or files.
 */
const CorrectionPanel: React.FC<{
  messageId: string;
  question: LinkedQuestion | null | undefined;
  /** What the visitor actually asked: the starting point for a new pair. */
  defaultQuestion?: string | null;
  sourceUrls: string[];
  onQuestionChange?: (question: LinkedQuestion) => void;
}> = ({ messageId, question, defaultQuestion, sourceUrls, onQuestionChange }) => {
  const { post, put } = useApi();
  const { user } = useUser();
  const initialQuestion = question?.question || defaultQuestion || '';
  const [questionText, setQuestionText] = useState(initialQuestion);
  const [answerText, setAnswerText] = useState(question?.answer || '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!question || (question.status !== 'answered' && question.status !== 'dismissed'));

  useEffect(() => {
    // A different (or newly saved / dismissed) linked question resets the form.
    setQuestionText(question?.question || defaultQuestion || '');
    setAnswerText(question?.answer || '');
    setEditing(!question || (question.status !== 'answered' && question.status !== 'dismissed'));
    // defaultQuestion deliberately excluded: it is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, question?.question, question?.answer, question?.status]);

  useEffect(() => {
    // The snapshot's query arrives after the drawer opens. Fill the question
    // only if it is still empty; never touch the answer the reviewer is typing.
    if (question?.question || !defaultQuestion) return;
    setQuestionText((current) => (current.trim() ? current : defaultQuestion));
  }, [defaultQuestion, question?.question]);

  const dismissed = !!question && question.status === 'dismissed';
  const corrected = !!question && (question.status === 'answered' || dismissed);
  const flagged = !!question && !corrected;
  const canSave = questionText.trim().length > 0 && answerText.trim().length > 0 && !saving;

  // "Not applicable": the extraction flagged something that isn't a real
  // knowledge gap (small talk, a request we don't serve, a misread). Keeps the
  // row so the transcript shows it reviewed and repeats link to it silently.
  const dismiss = async () => {
    if (!question || saving) return;
    setSaving(true);
    try {
      const response = await post(`/questions/${question.id}/dismiss`, {});
      const saved = response.data || {};
      onQuestionChange?.({
        ...question,
        status: 'dismissed',
        answered_at: saved.answered_at || new Date().toISOString(),
        answered_by: user
          ? { id: user.id, first_name: user.first_name, last_name: user.last_name, email: user.email }
          : question.answered_by || null,
      });
      setEditing(false);
      toast.success('Marked as not applicable.');
    } catch (error) {
      console.error('Error dismissing question:', error);
      toast.error('Could not update the question. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = { question: questionText.trim(), answer: answerText.trim() };
      const response = question
        ? await put(`/questions/${question.id}`, payload)
        : await post('/questions/', { ...payload, source_message_id: messageId });
      const saved = response.data || {};
      onQuestionChange?.({
        id: saved.id || question?.id,
        question: saved.question ?? payload.question,
        answer: saved.answer ?? payload.answer,
        status: saved.status || 'answered',
        extraction_reason: question?.extraction_reason ?? null,
        answered_at: saved.answered_at || new Date().toISOString(),
        answered_by: user
          ? { id: user.id, first_name: user.first_name, last_name: user.last_name, email: user.email }
          : question?.answered_by || null,
      });
      setEditing(false);
      toast.success('Answer saved. The agent will use it from now on.');
    } catch (error) {
      console.error('Error saving corrected answer:', error);
      toast.error('Could not save the answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const who = reviewerName(question);

  return (
    <div
      className={cn(
        'mt-4 rounded-lg border px-3.5 py-3 space-y-3',
        flagged && 'border-amber-500/50 bg-amber-500/[0.06]',
        corrected && 'border-emerald-500/50 bg-emerald-500/[0.06]',
        !question && 'border-border bg-muted/30'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-foreground">
            {dismissed ? 'Reviewed: not applicable' : corrected ? 'Answer corrected' : flagged ? 'The AI could not answer this' : 'Correct this answer'}
          </div>
          <div className="text-[12px] text-muted-foreground mt-0.5">
            {corrected && who && (
              <>{dismissed ? 'Marked not applicable' : 'Corrected'} by {who}{question?.answered_at ? ` on ${new Date(question.answered_at).toLocaleDateString()}` : ''}. </>
            )}
            {dismissed && <>No answer is needed for this one. You can still write one if that changes. </>}
            {flagged && question?.extraction_reason && <>{question.extraction_reason} </>}
            {!corrected && (
              <>Write the answer the agent should give. It applies to every agent in your organization and overrides what the AI would synthesize from your website.</>
            )}
          </div>
        </div>
        {corrected && !editing && (
          <Button type="button" variant="outline" size="sm" className="h-7 text-[12px] flex-shrink-0" onClick={() => setEditing(true)}>
            {dismissed ? 'Write an answer' : 'Edit'}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2.5">
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Question</label>
            <Textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="The question visitors ask, in general terms"
              className="min-h-[56px] text-[13px]"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Correct answer</label>
            <Textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="What the agent should say instead"
              className="min-h-[96px] text-[13px]"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              {flagged && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[12px] text-muted-foreground"
                  disabled={saving}
                  onClick={dismiss}
                  title="This flag isn't a real knowledge gap. Mark the reply reviewed without an answer."
                >
                  Not applicable
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {corrected && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[12px]"
                  onClick={() => {
                    setQuestionText(question?.question || '');
                    setAnswerText(question?.answer || '');
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button type="button" size="sm" className="h-7 text-[12px]" disabled={!canSave} onClick={save}>
                {saving ? 'Saving…' : 'Save answer'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Question</div>
            <div className="text-[13px] text-foreground whitespace-pre-wrap break-words">{question?.question}</div>
          </div>
          {!dismissed && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Answer</div>
              <div className="text-[13px] text-foreground whitespace-pre-wrap break-words">{question?.answer}</div>
            </div>
          )}
        </div>
      )}

      {sourceUrls.length > 0 && (
        <div className="border-t border-border/60 pt-2.5 text-[12px] text-muted-foreground">
          <div>
            The AI answered from these pages. If the website is wrong, fix it there — the agent retrains on your site daily, and this answer can be deleted afterwards.
          </div>
          <ul className="mt-1 space-y-0.5">
            {sourceUrls.map((url) => (
              <li key={url} className="truncate">
                <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{url}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Curated, customer-friendly labels for tools (raw names are dev-facing).
const TOOL_LABELS: Record<string, string> = {
  search_knowledge: "Knowledge base search",
  search_task_definitions: "Task search",
  show_booking_calendar: "Booking calendar",
  escalate_to_human: "Human handoff",
  trigger_buyer_intent_notification: "Buyer-intent alert",
  update_form: "Form capture",
  get_company_info: "Company lookup",
  navigate: "Page navigation",
  start_onboarding: "Onboarding",
  start_assessment: "Assessment",
  record_assessment_response: "Assessment",
  pause_assessment: "Assessment",
};

const prettyToolName = (name?: string): string => {
  if (!name) return "Tool";
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];
  return name.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Sleek, compact markdown styling for the curated context document.
const mdComponents = {
  h1: (p: any) => <h3 className="text-[13px] font-semibold text-foreground mt-5 first:mt-0 mb-2" {...p} />,
  h2: (p: any) => <h3 className="text-[13px] font-semibold text-foreground mt-5 first:mt-0 mb-2" {...p} />,
  h3: (p: any) => <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-4 mb-1.5" {...p} />,
  p: (p: any) => <p className="text-[13px] leading-relaxed text-foreground/90 mb-2.5" {...p} />,
  ul: (p: any) => <ul className="list-disc pl-4 text-[13px] text-foreground/90 space-y-1 mb-2.5" {...p} />,
  ol: (p: any) => <ol className="list-decimal pl-4 text-[13px] text-foreground/90 space-y-1 mb-2.5" {...p} />,
  li: (p: any) => <li className="leading-relaxed" {...p} />,
  a: (p: any) => <a className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  strong: (p: any) => <strong className="font-semibold text-foreground" {...p} />,
  code: (p: any) => <code className="rounded bg-muted px-1 py-0.5 text-[12px] font-mono" {...p} />,
  hr: () => <div className="h-px bg-border my-4" />,
};

// One stage in the vertical "what went in → what came out" flow. A numbered icon
// node sits on a connecting spine so the whole drawer reads top-to-bottom as the
// ordered pipeline the AI actually ran: prior turns → message → instructions →
// tools → output.
const Step: React.FC<{
  icon: React.ElementType;
  label: string;
  accent?: boolean;
  isLast?: boolean;
  children: React.ReactNode;
}> = ({ icon: Icon, label, accent, isLast, children }) => (
  <div className={cn("relative pl-11", !isLast && "pb-6")}>
    <div
      className={cn(
        "absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full border",
        accent
          ? "border-primary/30 bg-primary/10 text-primary"
          : "border-border bg-muted text-muted-foreground"
      )}
    >
      <Icon className="h-4 w-4" />
    </div>
    {!isLast && <div className="absolute left-4 top-9 bottom-1 w-px -translate-x-1/2 bg-border" />}
    <div className="mb-2.5 mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
    {children}
  </div>
);

const REDACTION_COPY: Record<string, { title: string; desc: string }> = {
  system: {
    title: "Aimdoc system instructions",
    desc: "Hidden to keep the assistant secure against prompt-injection and misuse.",
  },
  page_content: {
    title: "Page content",
    desc: "The processed page the visitor was viewing is withheld here.",
  },
};

const RedactedCard: React.FC<{ reason?: string }> = ({ reason }) => {
  const copy = REDACTION_COPY[reason || 'system'] || REDACTION_COPY.system;
  const Icon = reason === 'page_content' ? FileLock2 : Lock;
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-dashed border-border bg-muted/40 px-3.5 py-3">
      <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <div className="space-y-0.5">
        <div className="text-[12px] font-medium text-foreground">{copy.title}</div>
        <div className="text-[11px] text-muted-foreground leading-relaxed">{copy.desc}</div>
      </div>
    </div>
  );
};

// One entry in the reconstructed "Earlier in the conversation" thread — faithful
// to what the AI received: visitor/assistant text, tool calls, tool outputs, with
// Aimdoc-internal items redacted.
const ConversationEntryRow: React.FC<{ entry: ConversationEntry }> = ({ entry }) => {
  if (entry.kind === 'tool_call') {
    if (entry.redacted) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2">
          <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="text-[11px] text-muted-foreground">Internal Aimdoc action — redacted</span>
        </div>
      );
    }
    const args = entry.args && typeof entry.args === 'object' ? entry.args : null;
    return (
      <div className="rounded-lg border border-border bg-background px-3 py-2">
        <div className="flex items-center gap-1.5 text-[12px] font-medium text-foreground">
          <Wrench className="h-3 w-3 text-muted-foreground" />
          Called {prettyToolName(entry.tool_name)}
        </div>
        {args && Object.keys(args).length > 0 && (
          <div className="mt-1 space-y-0.5">
            {Object.entries(args).map(([k, v]) => (
              <div key={k} className="flex gap-1.5 text-[11px]">
                <span className="text-muted-foreground shrink-0">{k}:</span>
                <span className="text-foreground/80 break-words [overflow-wrap:anywhere]">
                  {typeof v === 'string' ? v : JSON.stringify(v)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (entry.kind === 'tool_output') {
    if (entry.redacted) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2">
          <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="text-[11px] text-muted-foreground">Tool result — redacted</span>
        </div>
      );
    }
    return (
      <div className="rounded-lg bg-muted/40 px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
          {prettyToolName(entry.tool_name)} result
        </div>
        <div className="text-[11px] text-foreground/70 leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-h-40 overflow-auto">
          {entry.content}
        </div>
      </div>
    );
  }

  // text (visitor / assistant)
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
        {entry.role === 'assistant' ? 'Assistant' : 'Visitor'}
      </div>
      <div className="text-[12px] text-foreground/80 leading-relaxed whitespace-pre-wrap break-words">
        {entry.content}
      </div>
    </div>
  );
};

const ContextDrawer: React.FC<ContextDrawerProps> = ({ messageId, open, onClose, responseContent, toolCall, isAdmin = false, hasSnapshot = true, question, onQuestionChange, visitorQuestion }) => {
  const { get } = useApi();
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState<ContextSnapshot | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [queryExpanded, setQueryExpanded] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);

  useEffect(() => {
    if (!open || !messageId) return;
    if (!hasSnapshot) {
      // Pre-capture reply: nothing to fetch, the drawer only offers the correction.
      setSnapshot(null);
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Reset expand state each time the drawer opens for a (possibly new) message.
    setQueryExpanded(false);
    setShowPrevious(false);

    const fetchContext = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const path = `/messages/${messageId}/context${isAdmin ? '?is_admin=true' : ''}`;
        const response = await get(path);
        if (cancelled) return;
        if (response.data && response.data.id) {
          setSnapshot(response.data);
        } else {
          setSnapshot(null);
          setNotFound(true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching message context:', error);
        setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchContext();
    return () => { cancelled = true; };
  }, [open, messageId, isAdmin, hasSnapshot]);

  const tools = snapshot?.tools || [];
  const content = snapshot?.content || [];
  const query = snapshot?.query || '';
  const previousMessages = snapshot?.previous_messages || [];
  const intermediateSteps = snapshot?.intermediate_steps || [];
  const queryIsLong = query.length > 280;

  // Admin keeps the raw, unstyled view (internal — does not need to be pretty).
  const adminSystemText = (snapshot?.system_segments || []).map((s) => s.text || '').join('').trim();
  const adminInput = snapshot?.input || null;
  const adminMessages = adminInput?.messages || [];
  const adminPruned = adminInput?.pruned || [];

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[50vw] overflow-y-auto">
        <SheetHeader className="space-y-1">
          <SheetTitle className="text-base">Why the AI responded this way</SheetTitle>
          <SheetDescription className="text-[13px]">
            Everything that shaped this response, start to finish — from what came in to what the assistant decided.
          </SheetDescription>
        </SheetHeader>

        {/* Text replies (not tool calls) can be corrected in place. Shown above
            the analysis so a flagged reply leads with the fix, and even when
            no snapshot was captured. */}
        {!toolCall && !isAdmin && (
          <CorrectionPanel
            messageId={messageId}
            question={question}
            defaultQuestion={(snapshot?.query || '').trim() || visitorQuestion || null}
            sourceUrls={extractSourceUrls(intermediateSteps)}
            onQuestionChange={onQuestionChange}
          />
        )}

        {!loading && snapshot?.moderation_scan && (
          <ModerationScanCard scan={snapshot.moderation_scan} />
        )}

        {loading && (
          <div className="text-sm text-muted-foreground text-center py-16">Analyzing…</div>
        )}

        {!loading && notFound && (
          <div className="text-sm text-muted-foreground text-center py-16">
            No context was captured for this message.
          </div>
        )}

        {/* ---------- Customer view: one vertical flow, in order ---------- */}
        {!loading && snapshot && !isAdmin && (() => {
          const hasReply = !!(responseContent && responseContent.trim());
          const hasOutput = !!toolCall || hasReply;

          // Build the ordered list of stages that are actually present, so the
          // connecting spine knows which node is last.
          const stages: { key: string; icon: React.ElementType; label: string; accent?: boolean; body: React.ReactNode }[] = [];

          if (previousMessages.length > 0) {
            stages.push({
              key: 'history',
              icon: MessagesSquare,
              label: 'Conversation so far',
              body: (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowPrevious((v) => !v)}
                    className="flex items-center gap-1.5 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showPrevious && "rotate-180")} />
                    {showPrevious
                      ? 'Hide messages'
                      : `Show ${previousMessages.length} earlier message${previousMessages.length > 1 ? 's' : ''}`}
                  </button>
                  {showPrevious && (
                    <div className="mt-3 space-y-2">
                      {previousMessages.map((entry, idx) => (
                        <ConversationEntryRow key={idx} entry={entry} />
                      ))}
                    </div>
                  )}
                </div>
              ),
            });
          }

          if (query) {
            stages.push({
              key: 'query',
              icon: MessageCircle,
              // A tool call isn't "answering" a chat message — it's acting on the
              // conversation so far — so reframe the label for tool-call snapshots.
              label: toolCall ? 'Message it’s acting on' : 'Message it’s answering',
              accent: true,
              body: (
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
                  <div className="relative">
                    <div className={cn(
                      "whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground",
                      queryIsLong && !queryExpanded && "max-h-32 overflow-hidden"
                    )}>
                      {query}
                    </div>
                    {queryIsLong && !queryExpanded && (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-primary/5 to-transparent" />
                    )}
                  </div>
                  {queryIsLong && (
                    <button
                      type="button"
                      onClick={() => setQueryExpanded((v) => !v)}
                      className="mt-2 text-[11px] font-medium text-primary hover:underline"
                    >
                      {queryExpanded ? 'Show less' : 'Show full message'}
                    </button>
                  )}
                </div>
              ),
            });
          }

          if (intermediateSteps.length > 0) {
            stages.push({
              key: 'steps',
              icon: ListChecks,
              // Tool calls/outputs the AI ran after the message but before this
              // response — the work it did on the way to the answer.
              label: 'Steps it took',
              body: (
                <div className="space-y-2">
                  {intermediateSteps.map((entry, idx) => (
                    <ConversationEntryRow key={idx} entry={entry} />
                  ))}
                </div>
              ),
            });
          }

          stages.push({
            key: 'instructions',
            icon: Shield,
            label: 'Instructions it followed',
            body: (
              <div className="space-y-2.5">
                {content.length === 0 && (
                  <div className="text-[13px] text-muted-foreground">No additional instructions for this response.</div>
                )}
                {content.map((block, idx) =>
                  block.type === 'redacted' ? (
                    <RedactedCard key={idx} reason={block.reason} />
                  ) : (
                    <div key={idx} className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                      <Markdown remarkPlugins={[remarkBreaks]} components={mdComponents}>
                        {block.markdown || ''}
                      </Markdown>
                    </div>
                  )
                )}
              </div>
            ),
          });

          if (tools.length > 0) {
            stages.push({
              key: 'tools',
              icon: Wrench,
              label: 'Tools it could use',
              body: (
                <div className="flex flex-wrap gap-1.5">
                  {tools.map((tool, idx) => (
                    <span
                      key={idx}
                      title={tool.description || undefined}
                      className="inline-flex items-center rounded-full border border-border bg-muted/50 px-3 py-1 text-[12px] font-medium text-foreground"
                    >
                      {prettyToolName(tool.name)}
                    </span>
                  ))}
                </div>
              ),
            });
          }

          if (hasOutput) {
            stages.push({
              key: 'output',
              icon: Sparkles,
              label: 'What it did',
              accent: true,
              body: toolCall ? (
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
                  <div className="mb-2 text-[13px] text-foreground">
                    Called <span className="font-semibold">{prettyToolName(toolCall.name)}</span>
                  </div>
                  {toolCall.args && Object.keys(toolCall.args).length > 0 && (
                    <div className="space-y-1">
                      {Object.entries(toolCall.args).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-[12px]">
                          <span className="shrink-0 text-muted-foreground">{k}:</span>
                          <span className="break-words text-foreground/90 [overflow-wrap:anywhere]">
                            {typeof v === 'string' ? v : JSON.stringify(v)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
                  <Markdown remarkPlugins={[remarkBreaks]} components={mdComponents}>
                    {responseContent || ''}
                  </Markdown>
                </div>
              ),
            });
          }

          return (
            <div className="mt-6">
              {stages.map((stage, idx) => (
                <Step
                  key={stage.key}
                  icon={stage.icon}
                  label={stage.label}
                  accent={stage.accent}
                  isLast={idx === stages.length - 1}
                >
                  {stage.body}
                </Step>
              ))}
            </div>
          );
        })()}

        {/* ---------- Admin view: raw, full fidelity (internal) ---------- */}
        {!loading && snapshot && isAdmin && (
          <div className="mt-4 space-y-6">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">System prompt (raw)</h3>
              <div className="text-xs bg-muted p-3 rounded-lg whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                {adminSystemText || '—'}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Available tools</h3>
              <div className="space-y-2">
                {tools.map((tool, idx) => (
                  <div key={idx} className="rounded-lg border border-border p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-medium">{tool.name}</span>
                      {tool.visibility === 'internal' && (
                        <Badge variant="outline" className="text-[10px] py-0">internal</Badge>
                      )}
                    </div>
                    {tool.description && (
                      <div className="text-[11px] text-muted-foreground mt-1 break-words">{tool.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {adminInput && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">
                    Conversation input{adminMessages.length ? ` (${adminMessages.length})` : ''}
                  </h3>
                  {adminInput.previous_response_id && (
                    <div className="text-[11px] text-muted-foreground">
                      Prior turns carried server-side via response <span className="font-mono">{adminInput.previous_response_id}</span>.
                    </div>
                  )}
                  <div className="space-y-2">
                    {adminMessages.map((m, idx) => (
                      <div key={idx} className="rounded-lg border border-border p-2.5">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px] py-0">{m.role || '—'}</Badge>
                          {m.type && m.type !== m.role && (
                            <span className="text-[10px] text-muted-foreground">{m.type}</span>
                          )}
                          {m.synthetic && <Badge variant="outline" className="text-[10px] py-0">synthetic</Badge>}
                          {m.missing && <Badge variant="outline" className="text-[10px] py-0 text-destructive">deleted</Badge>}
                        </div>
                        <div className="text-[11px] whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-h-40 overflow-auto">
                          {m.content || <span className="text-muted-foreground italic">(no text content)</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {adminPruned.length > 0 && (
                    <div className="space-y-1.5">
                      <h4 className="text-xs font-medium">Pruned / stale search results ({adminPruned.length})</h4>
                      {adminPruned.map((p, idx) => (
                        <div key={idx} className="rounded border border-border px-2.5 py-1.5 text-[11px]">
                          <span className="font-medium">{p.reason || p.type}</span>
                          {p.query && <span className="text-muted-foreground"> · “{p.query}”</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {(snapshot.model || snapshot.provider) && (
              <div className="text-[10px] text-muted-foreground/70 pt-2">
                {snapshot.provider}{snapshot.provider && snapshot.model ? ' · ' : ''}{snapshot.model}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ContextDrawer;
